<script setup lang="ts">
/**
 * The inner-page call to action from the template: a big headline with the
 * "union" glyph inline, and one large round lilac button whose arrow slides
 * out to the top-right on hover while a second one slides in. Background
 * shape bleeds in from the left on very wide screens; a hairline closes it.
 */
const props = defineProps<{
  title?: string
  to?: string
  /** Accessible name of the round button. */
  label?: string
}>()

const { t } = useI18n()
const { isAuthenticated } = useAuthUser()

const heading = computed(() => props.title || t('sofax.cta.title'))
const target = computed(() => props.to || (isAuthenticated.value ? '/cv' : '/free-cv-builder'))
const name = computed(() => props.label || t('sofax.cta.label'))
</script>

<template>
  <section class="sofax relative overflow-hidden bg-white dark:bg-gray-950">
    <div class="section-space relative">
      <div class="shell">
        <div class="flex flex-wrap items-center justify-center gap-10 sm:flex-nowrap sm:justify-between">
          <div class="sm:max-w-[51.25rem] sm:flex-1">
            <h2 class="text-center sm:text-left">
              {{ heading }}
              <img
                src="/sofax/icon-union.svg"
                alt=""
                aria-hidden="true"
                width="83"
                height="80"
                class="inline h-auto w-10 align-baseline lg:w-16"
              >
            </h2>
          </div>

          <LocaleLink
            :to="target"
            :aria-label="name"
            class="group relative inline-flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-lilac text-ink lg:size-[6.25rem] xl:size-[7.5rem]"
          >
            <span class="translate-x-0 translate-y-0 text-5xl transition-all duration-300 group-hover:-translate-y-full group-hover:translate-x-full lg:text-6xl">
              <i class="bi bi-arrow-up-right" />
            </span>
            <span class="absolute -translate-x-full translate-y-full text-5xl transition-all duration-300 group-hover:translate-x-0 group-hover:translate-y-0 lg:text-6xl">
              <i class="bi bi-arrow-up-right" />
            </span>
          </LocaleLink>
        </div>
      </div>

      <img
        src="/sofax/background-shape-1.svg"
        alt=""
        aria-hidden="true"
        width="273"
        height="204"
        class="pointer-events-none absolute left-5 top-1 -z-10 hidden 2xl:inline-block"
      >
    </div>

    <div class="separator" />
  </section>
</template>
